/**
 * Removes a leading line that just restates the field's section title/heading
 * from LLM-generated template language, so the Clinical Intake Form doesn't show
 * the heading twice (once as the bold row label, once as the first line of the
 * generated content). Tolerant of markdown heading markers, HTML tags (for the
 * CPT colour-coded HTML path), and reworded/renumbered restatements.
 */

const NBSP_RE = new RegExp(String.fromCharCode(160), 'g');

function normalizeHeadingForCompare(text) {
  return String(text || '')
    .replace(NBSP_RE, ' ')
    .replace(/^\s*\d+(?:\.\d+)*\.?\s*/, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function stringBigrams(s) {
  const grams = [];
  for (let i = 0; i < s.length - 1; i++) grams.push(s.slice(i, i + 2));
  return grams;
}

/** Sorensen-Dice coefficient over character bigrams -- same approach used for ICF export. */
function diceCoefficient(a, b) {
  const ga = stringBigrams(a);
  const gb = stringBigrams(b);
  if (!ga.length || !gb.length) return 0;
  const counts = new Map();
  gb.forEach((g) => counts.set(g, (counts.get(g) || 0) + 1));
  let matches = 0;
  ga.forEach((g) => {
    const c = counts.get(g) || 0;
    if (c > 0) {
      matches++;
      counts.set(g, c - 1);
    }
  });
  return (2 * matches) / (ga.length + gb.length);
}

function headingMatchesTitle(line, title) {
  const nl = normalizeHeadingForCompare(line);
  const nt = normalizeHeadingForCompare(title);
  if (!nl || !nt) return false;
  if (nl === nt) return true;
  if (nl.length < 8 || nt.length < 8) return false;
  return diceCoefficient(nl, nt) >= 0.65;
}

/**
 * @param {string} text - generated content (plain text, or sanitized CPT HTML with span/br only)
 * @param {string} sectionLabel - the row's displayed title, e.g. "11.1 Regulatory and Ethical Considerations"
 */
export function stripLeadingDuplicateHeading(text, sectionLabel) {
  if (!text || !sectionLabel) return text || '';
  const lines = String(text).split(/\r?\n/);

  let idx = 0;
  while (idx < lines.length && !lines[idx].replace(/<[^>]+>/g, '').trim()) idx++;
  if (idx >= lines.length) return text;

  const candidate = lines[idx]
    .replace(/<[^>]+>/g, '')
    .replace(/^#{1,6}\s*/, '')
    .replace(/\*\*/g, '')
    .trim();

  if (!headingMatchesTitle(candidate, sectionLabel)) return text;

  let end = idx + 1;
  while (end < lines.length && !lines[end].replace(/<[^>]+>/g, '').trim()) end++;
  return lines.slice(end).join('\n').trim();
}
